import { createContext, useContext, useEffect, useRef } from 'react';

/** 유효성 규칙: 유효하면 true, 아니면 에러 메시지 문자열 반환 (sd types/form Rule 포팅) */
export type Rule = (value: unknown) => true | string;

/** rules를 순서대로 실행해 첫 실패 메시지를 반환. 모두 통과하면 "" */
export function runRules(value: unknown, rules?: Rule[]): string {
  if (!rules || rules.length === 0) return '';
  for (const rule of rules) {
    const result = rule(value ?? '');
    if (result !== true) return typeof result === 'string' ? result : '유효하지 않은 값입니다.';
  }
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
// SForm 연동 — Context 기반 필드 등록 (sd-form 검증 서브시스템 포팅)
// 각 rule-bearing 컨트롤이 useFormField로 자신을 등록하면, SForm이 submit 시
// 등록된 모든 필드의 validate()를 실행해 하나라도 실패하면 제출을 막는다.
// SForm 밖에서 쓰이면 context가 없어 no-op (form-agnostic 사용 유지).
// ─────────────────────────────────────────────────────────────────────────────

/** SForm에 등록되는 필드 항목 */
export interface FormFieldEntry {
  /** 폼 연동용 name (validationError names·focus 대상 식별) */
  name: string;
  /** 현재 값에 rules를 실행하고 에러 상태를 반영한 뒤 valid 여부 반환 */
  validate: () => boolean;
  /** 검증 에러 상태만 초기화 (값은 소비자 소유) */
  resetValidation: () => void;
}

export interface FormContextValue {
  /** 필드를 등록하고 해제 함수를 반환 */
  register: (entry: FormFieldEntry) => () => void;
}

export const FormContext = createContext<FormContextValue | null>(null);

/** 가장 가까운 SForm의 context (없으면 null) */
export function useFormContext(): FormContextValue | null {
  return useContext(FormContext);
}

/**
 * 폼 필드 등록 훅. SForm 내부이면 등록되고, 밖이면 no-op.
 * validate/resetValidation은 항상 최신 클로저를 참조하도록 ref로 래핑한다.
 */
export function useFormField(field: {
  name?: string;
  validate: () => boolean;
  resetValidation: () => void;
}): void {
  const ctx = useFormContext();
  const cbRef = useRef(field);
  cbRef.current = field;
  const name = field.name ?? '';

  useEffect(() => {
    if (!ctx) return;
    return ctx.register({
      name,
      validate: () => cbRef.current.validate(),
      resetValidation: () => cbRef.current.resetValidation(),
    });
  }, [ctx, name]);
}
